import {
  LuxButton,
  LuxDivider,
  LuxLoader,
  LuxSelectablePill,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import useCustomTranslation from '@hooks/useCustomTranslation';
import useGetAppointments from '@hooks/useGetAppointments';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { ServicesModal } from './ServicesModal';
import { CSSProperties, Fragment, useState } from 'react';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { AppointmentOption } from '@components/AppointmentTypePage/AppointmentTypePage.styles';
import { StepContainer } from '@components/Steps/Steps.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';

interface ServicesCardProps {
  readonly showAcceptedInsurancesFirst: boolean;
}

interface ServiceSectionProps {
  title: string;
  items: string[];
  emptyMessage: string;
  isLoading: boolean;
  isMobile: boolean;
  wrapText?: boolean;
}

interface ServiceSectionConfig extends Omit<ServiceSectionProps, 'isMobile'> {
  id: string;
  visible: boolean;
}

const MAX_VISIBLE_ITEMS = 10;

function getItemsSummary(
  items: string[],
  overflowLabel: string,
  emptyMessage: string,
) {
  if (items.length === 0) return emptyMessage;

  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS).join('; ');
  const remainingItems = items.length - MAX_VISIBLE_ITEMS;

  return remainingItems > 0
    ? `${visibleItems}; +${remainingItems} ${overflowLabel}`
    : visibleItems;
}

function ServiceSection({
  title,
  items,
  emptyMessage,
  isLoading,
  isMobile,
  wrapText = false,
}: ServiceSectionProps) {
  const theme = useTheme();
  const textStyle: CSSProperties = {
    ...(!isMobile && { textAlign: 'left' }),
    ...(wrapText && {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    }),
  };

  const content = (
    <Stack flexDirection="column" gap="0.5rem" width="100%">
      {isMobile ? (
        <LuxTypography variant="textMedium">{title}</LuxTypography>
      ) : (
        <LuxSelectablePill
          text={title}
          selectable={false}
          customColor={theme.palette.LUX.obTokens.bgColorBaseSecondary}
          customBorderColor={
            theme.palette.LUX.obTokens.bordersColorNeutralSecondary
          }
        />
      )}
      {isLoading && !isMobile ? (
        <LuxLoader size="small" />
      ) : (
        <LuxTypography variant="textXs" style={textStyle}>
          {getItemsSummary(items, title, emptyMessage)}
        </LuxTypography>
      )}
    </Stack>
  );

  return isMobile ? (
    content
  ) : (
    <AppointmentOption
      style={{ cursor: 'unset' }}
      selectedColor={theme.palette.LUX.obTokens?.bordersColorBrandPrimary}
      color={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
      minHeight
    >
      {content}
    </AppointmentOption>
  );
}

export function ServicesCard({
  showAcceptedInsurancesFirst,
}: ServicesCardProps) {
  const [openModal, setOpenModal] = useState(false);
  const { t } = useCustomTranslation();
  const theme = useTheme();
  const practiceConfig = useOnlineBookingStore(state => state.generalConfig);
  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const practiceId = useOnlineBookingStore(state => state.practiceId);

  const { data: insurances, isLoading: isLoadingInsurances } =
    useGetInsuranceConfiguration({
      organizationId,
    });

  const { data: appointments, isLoading: isLoadingAppt } = useGetAppointments({
    practiceIdValue: practiceId,
  });

  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);
  const showAcceptedInsurances =
    practiceConfig?.practiceInformation?.showAcceptedInsurances;
  const showExamServices =
    practiceConfig?.practiceInformation?.showExamServices;

  const acceptedInsurancesSection: ServiceSectionConfig = {
    id: 'accepted-insurances',
    visible: Boolean(showAcceptedInsurances),
    title: t('moreInfosModal.acceptedInsurances2'),
    items: (insurances ?? []).map(insurance => insurance.name),
    emptyMessage: t('No insurances'),
    isLoading: isLoadingInsurances,
    wrapText: true,
  };

  const appointmentTypesSection: ServiceSectionConfig = {
    id: 'appointment-types',
    visible: Boolean(showExamServices),
    title: t('practiceCard.appTypes'),
    items: (appointments ?? []).map(appointment => appointment.title),
    emptyMessage: t('No appointments'),
    isLoading: isLoadingAppt,
  };

  const serviceSections = (
    showAcceptedInsurancesFirst
      ? [acceptedInsurancesSection, appointmentTypesSection]
      : [appointmentTypesSection, acceptedInsurancesSection]
  ).filter(section => section.visible);

  return (
    <>
      {(showExamServices || showAcceptedInsurances) && (
        <StepContainer>
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4">
              {t('practiceCard.services')}
            </LuxTypography>
            {!isMobile && showExamServices && (
              <LuxButton onClick={() => setOpenModal(true)} variant="secondary">
                {t('practiceCard.viewAllAppointments')}
              </LuxButton>
            )}
          </CardTitleWrapper>
          <Stack padding="0.5rem 1rem 1rem 1rem" gap="1rem">
            {serviceSections.map(
              (
                { id, title, items, emptyMessage, isLoading, wrapText },
                index,
              ) => (
                <Fragment key={id}>
                  {isMobile && index > 0 && <LuxDivider />}
                  <ServiceSection
                    title={title}
                    items={items}
                    emptyMessage={emptyMessage}
                    isLoading={isLoading}
                    isMobile={isMobile}
                    wrapText={wrapText}
                  />
                </Fragment>
              ),
            )}
            {isMobile && showExamServices && (
              <LuxButton onClick={() => setOpenModal(true)} variant="secondary">
                {t('practiceCard.viewAllAppointments')}
              </LuxButton>
            )}
          </Stack>
        </StepContainer>
      )}
      <ServicesModal
        isOpen={openModal}
        appointmentTypes={appointments ?? []}
        onClose={() => setOpenModal(false)}
      />
    </>
  );
}
